import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { buildUsersPath } from "@/core/constants/paths";
import { ROLES_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useTableSettings, useToast } from "@/ds";
import type { FilterValue, HubTableDataTableProps } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import type { Role } from "@/modules/roles";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { CONTROL_PANEL_MOBILE_QUERY } from "../control-panel.constants";
import { usePermissionOptions } from "./hooks/use-permission-options";
import { useRolesExclusao } from "./hooks/use-roles-exclusao";
import { useRolesExportacao } from "./hooks/use-roles-exportacao";
import { useRolesFiltros } from "./hooks/use-roles-filtros";
import { useRolesGravacao } from "./hooks/use-roles-gravacao";
import { useRolesLista } from "./hooks/use-roles-lista";
import {
  isResourceRole,
  RESOURCE_ROLE_DELETE_TOOLTIP,
  ROLES_ALL_COLUMNS,
  ROLES_COLUMNS,
  ROLES_PERMISSION_FILTER_KEY,
  ROLES_TABLE_LABEL,
  ROLES_TABLE_MIN_WIDTH,
} from "./roles.constants";
import type { RolesColumnKey } from "./roles.constants";
import type { RoleColuna, RoleLinha, RolesViewModel } from "./types-roles";

const DELETE_TOOLTIP = "Excluir";

const COLUNAS: readonly RoleColuna[] = ROLES_COLUMNS.map((coluna) =>
  coluna.key === "permissoes"
    ? { ...coluna, key: coluna.key, celula: "tags" }
    : { ...coluna, key: coluna.key, celula: "texto" },
);

/**
 * Aba Perfis do Painel (HU 722, 864, 870 e a exportação da 1442): lista, cadastro, edição, exclusão e menu ⋮. Cadastro,
 * edição e exclusão só existem com `hub.perfil:edit` (ADR 0010).
 */
export function useRoles(): RolesViewModel {
  const isMobile = useMediaQuery(CONTROL_PANEL_MOBILE_QUERY);
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermissao } = usePermissoes();
  const podeEditar = hasPermissao(HUB_PERMISSOES.PERFIL_EDIT);
  const [formulario, setFormulario] = useState<{ role: Role | null } | null>(null);
  const [excluindo, setExcluindo] = useState<Role | null>(null);
  const [bloqueado, setBloqueado] = useState<Role | null>(null);

  const settings = useTableSettings<RolesColumnKey>({
    densityKey: ROLES_TABLE_DENSITY_KEY,
    allColumns: ROLES_ALL_COLUMNS,
  });
  const notifySettingsApplied = useTableSettingsAppliedToast(toast);
  const permissoes = usePermissionOptions();
  const lista = useRolesLista();
  const filterColumns = useRolesFiltros({
    options: permissoes.options,
    isLoading: permissoes.isLoading,
    hasError: permissoes.hasError,
  });
  const exportView = useRolesExportacao({
    colunas: settings.visibleColumns,
    search: lista.busca,
    claims: lista.claims,
    sort: lista.appliedSort,
    filterColumns,
    isEmpty: lista.status === "success" && lista.total === 0,
    toast,
  });
  const gravar = useRolesGravacao({
    toast,
    onSaved: () => {
      setFormulario(null);
      lista.reload();
    },
  });
  const apagar = useRolesExclusao({
    toast,
    onDeleted: () => {
      setExcluindo(null);
      lista.reload();
    },
    onBlocked: (role) => {
      setExcluindo(null);
      setBloqueado(role);
    },
  });

  const linhas = useMemo<RoleLinha[]>(
    () =>
      lista.items.map((role) => ({
        id: role.id,
        role,
        editLabel: `Editar perfil ${role.nome}`,
        editTestId: `roles-edit-${role.id}`,
        deleteLabel: `Excluir perfil ${role.nome}`,
        deleteTestId: `roles-delete-${role.id}`,
        deleteDisabled: isResourceRole(role),
        deleteTooltip: isResourceRole(role) ? RESOURCE_ROLE_DELETE_TOOLTIP : DELETE_TOOLTIP,
      })),
    [lista.items],
  );

  // O corpo rola dentro do cartão; abaixo da largura mínima a tabela rola na horizontal.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    scrollHeight: "flex",
    tableStyle: { minWidth: ROLES_TABLE_MIN_WIDTH },
    "aria-label": ROLES_TABLE_LABEL,
  };

  const filters: FilterValue = useMemo(
    () => ({ [ROLES_PERMISSION_FILTER_KEY]: lista.claims }),
    [lista.claims],
  );

  return {
    isMobile,
    search: lista.search,
    buscar: lista.buscar,
    filterColumns,
    filters,
    applyFilters: lista.applyFilters,
    settings,
    exportView,
    notifySettingsApplied,
    colunas: COLUNAS,
    items: linhas,
    total: lista.total,
    isFirstLoad: lista.status === "loading" && linhas.length === 0,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    table: lista.tabela,
    dataTableProps,
    hasSearch: lista.hasSearch,
    hasFilters: lista.hasFilters,
    limpar: lista.hasSearch ? lista.clearSearch : lista.hasFilters ? lista.clearFilters : undefined,
    catalogo: {
      permissions: permissoes.permissions,
      isLoading: permissoes.isLoading,
      hasError: permissoes.hasError,
    },
    podeEditar,
    formulario,
    novo: () => setFormulario({ role: null }),
    editar: podeEditar ? (linha) => setFormulario({ role: linha.role }) : undefined,
    fecharFormulario: () => setFormulario(null),
    salvar: gravar,
    excluindo,
    bloqueado,
    excluir: (linha) => {
      if (linha.role.quantidadeUsuarios > 0) setBloqueado(linha.role);
      else setExcluindo(linha.role);
    },
    cancelarExclusao: () => setExcluindo(null),
    confirmarExclusao: apagar,
    fecharBloqueio: () => setBloqueado(null),
    irParaUsuarios: (role) => {
      setBloqueado(null);
      navigate(buildUsersPath({ papelId: role.id }));
    },
  };
}
